import { Component } from 'react';

import observer from 'saddlebag-observer';

import BpkHorizontalNav, {
  BpkHorizontalNavItem,
} from '@skyscanner/backpack-web/bpk-component-horizontal-nav';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { onSearchControlsSubmit } from '../utils';

import type { NavElements } from '../Navigation/navigation-state';
import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable, Stay } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';
import type { NavigationItems } from '@shared/utils-frontend/navigation-service';

import STYLES from './Navigation.module.scss';

import SearchBar from '@shared/hotels-search-controls/SearchBar';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import {
  LOCATION,
  REVIEWS,
  GOOD_TO_KNOW,
  HOTEL_LOCATION_ANCHOR,
  HOTEL_REVIEWS_ANCHOR,
  scrollToElement,
} from '@shared/utils-frontend/navigation-service';
import { getScrollY } from '@shared/utils-frontend/window';

const cls = cssModules(STYLES);

const MOBILE_OFFSET = 1;
const REVIEWS_SCROLL_COMPENSATION_PX = 24;

type Props = {
  i18n: I18nService;
  primaryHeaderHeight: number;
  miniEventTracker: MiniEventTracker;
  items: NavigationItems;
  stay: Stay;
  userContext: UserContext;
  configs: Configs;
  destination: Destination;
};

type State = {
  needFixed: boolean;
  selected: Nullable<string>;
  isReviewsEmpty: boolean;
};

const SELF_HEIGHT = 42;

class Navigation extends Component<Props, State> {
  navElements: Nullable<NavElements>;

  setReviewsResultHandler: Nullable<Function>;

  seoVariantConfig: boolean;

  constructor(props: Props) {
    super(props);

    this.state = {
      selected: this.props.items[0].id,
      needFixed: false,
      isReviewsEmpty: false,
    };

    this.navElements = null;
    this.setReviewsResultHandler = null;
    this.seoVariantConfig =
      props.userContext.enableSEOVariantHotelsDetailsPage &&
      props.configs.showStickySearchControlsOnHotelDetailsSEO;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.navElements = this.props.items.map(({ anchorName, id }) => ({
      id,
      element: document.getElementById(anchorName),
    }));

    this.setReviewsResultHandler = observer.subscribe(
      'hotel-reviews-result-is-empty',
      ({ isReviewsEmpty }: { isReviewsEmpty: boolean }) => {
        this.setState({ isReviewsEmpty });
      },
    );
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    observer.unsubscribe(this.setReviewsResultHandler);
  }

  onScroll = () => {
    window.requestAnimationFrame(this.onRequestAnimationFrame);
  };

  getSelectedElement = (scrollY: number) => {
    const { primaryHeaderHeight } = this.props;
    const selectedElements =
      this.navElements &&
      this.navElements.filter(
        (item: { id: string; element: Nullable<HTMLElement> }) => {
          if (!item.element) return false;
          const compensation =
            item.id === REVIEWS ? REVIEWS_SCROLL_COMPENSATION_PX : 0;
          return (
            scrollY >=
            item.element.offsetTop -
              SELF_HEIGHT -
              primaryHeaderHeight -
              compensation
          );
        },
      );
    if (!selectedElements || selectedElements.length === 0) {
      return null;
    }

    return selectedElements[selectedElements.length - 1];
  };

  getNeedFixed = (scrollY: number) => {
    const { primaryHeaderHeight } = this.props;
    if (
      !this.navElements ||
      this.navElements.length === 0 ||
      !this.navElements[0].element
    ) {
      return false;
    }
    const { offsetTop } = this.navElements[0].element;
    return scrollY >= offsetTop - SELF_HEIGHT - primaryHeaderHeight;
  };

  getSelected = (scrollY: number) => {
    const selectedElement = this.getSelectedElement(scrollY);
    if (!selectedElement) {
      return null;
    }

    const { id } = selectedElement;
    return id;
  };

  onRequestAnimationFrame = () => {
    if (!this.navElements) {
      return;
    }

    const scrollY = getScrollY();
    const stickySearchOffset = this.seoVariantConfig ? 70 : 0;
    const state = {
      needFixed: this.getNeedFixed(scrollY),
      selected: this.getSelected(scrollY + stickySearchOffset),
    };
    const needToUpdateStates = Object.keys(state)
      // @ts-expect-error missing properties temporarily
      .filter((key) => state[key] !== this.state[key])
      // @ts-expect-error missing properties temporarily
      .reduce((acc, key) => ({ ...acc, [key]: state[key] }), {});

    if (Object.keys(needToUpdateStates).length) {
      this.setState(state);

      if (state.selected === REVIEWS) {
        this.props.miniEventTracker.trackHotelsAction(
          ACTION_TYPE.DETAILS_REVIEW_SECTION_SEEN,
        );
      }
    }
  };

  onClick = (anchorName: string) => {
    const { configs, primaryHeaderHeight, userContext } = this.props;
    let height = SELF_HEIGHT + primaryHeaderHeight - MOBILE_OFFSET;

    if (
      userContext.enableSEOVariantHotelsDetailsPage &&
      configs.showStickySearchControlsOnHotelDetailsSEO
    ) {
      height += 68;
    }

    if (anchorName === HOTEL_REVIEWS_ANCHOR) {
      height += REVIEWS_SCROLL_COMPENSATION_PX;
    }

    scrollToElement(
      anchorName === GOOD_TO_KNOW ? HOTEL_LOCATION_ANCHOR : anchorName,
      height,
    );
  };

  getNavigationItems = () => {
    const { isReviewsEmpty } = this.state;
    const { items } = this.props;
    const navigationItems = isReviewsEmpty
      ? items.map((item) => {
          if (item.id === REVIEWS) {
            return { ...item, isShow: false };
          }

          if (item.id === LOCATION) {
            return { ...item, mobile: true };
          }

          return item;
        })
      : items;

    return navigationItems.filter(({ isShow, mobile }) => isShow && mobile);
  };

  render() {
    const { destination, i18n, primaryHeaderHeight, stay } = this.props;
    const { needFixed, selected } = this.state;
    const navigationItems = this.getNavigationItems();

    return (
      <div
        className={cls(
          'Navigation',
          needFixed ? 'Navigation__showAndFix' : 'Navigation__hide',
        )}
        // @ts-expect-error '--primary-header-height' does not exist in type Props'
        style={{ '--primary-header-height': `${primaryHeaderHeight}px` }}
      >
        {this.seoVariantConfig && (
          <SearchBar
            stay={stay}
            isMobile
            arrangeInline
            onSearchControlsSubmit={onSearchControlsSubmit}
            destination={destination}
          />
        )}
        <BpkHorizontalNav className={cls('Navigation__nav')}>
          {navigationItems.map(({ anchorName, id, text }) => (
            <BpkHorizontalNavItem
              key={id}
              spaceAround
              className={cls('Navigation__bar')}
              data-test-id={`${id}-navigation`}
              selected={selected === id}
              onClick={(e: Event) => {
                e.preventDefault();
                this.onClick(anchorName);
              }}
              href={`#${anchorName}`}
            >
              <BpkText
                textStyle={TEXT_STYLES.label2}
                tagName="p"
                className={cls('Navigation__barText')}
                color={TEXT_COLORS.textPrimary}
              >
                {i18n.translate(text)}
              </BpkText>
            </BpkHorizontalNavItem>
          ))}
        </BpkHorizontalNav>
      </div>
    );
  }
}

export default withUserContext(
  withConfiguration(withMiniEventTracker(withI18n(Navigation))),
);
